import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import CssBaseline from '@mui/material/CssBaseline';
import InfoIcon from '@mui/icons-material/Info';
import CodeIcon from '@mui/icons-material/Code';
import GroupsIcon from '@mui/icons-material/Groups';
import UpdateIcon from '@mui/icons-material/Update';
import AppNavbar from '../dashboard/components/AppNavbar';
import SideMenu from '../dashboard/components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { DataModeProvider, DATA_MODES } from '../context/DataModeContext';
import { GrowProvider } from '../dashboard/context/GrowContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function AboutContent() {
  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '900px' }, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Stack spacing={1} sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
          About myGrow Collective
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          An app for monitoring, analyzing, and sharing your grows and experience.
        </Typography>
      </Stack>

      {/* Mission Card */}
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <InfoIcon sx={{ color: 'primary.main', mt: 0.5 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 1 }}>
              Our Mission
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
              myGrow Collective is dedicated to providing growers with the tools they need to monitor, analyze,
              and share their growing experience. From real-time sensor monitoring to smart automation and
              community-driven insights, we help you grow better, smarter, and more efficiently.
            </Typography>
          </Box>
        </Stack>
      </Card>

      {/* Features Card */}
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <CodeIcon sx={{ color: 'primary.main', mt: 0.5 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 2 }}>
              Key Features
            </Typography>
            <Stack spacing={1.5}>
              {[
                'Real-time sensor monitoring (pH, EC, temperature, humidity, CO2, and more)',
                'Grow cycle tracking from seedling to harvest',
                'Customizable sensor range presets for different growth stages',
                'Smart reminders and task management',
                'Analytics and insights for optimizing your grow',
                'Community sharing through The Collective',
                'Cloud sync across all your devices',
              ].map((feature, index) => (
                <Typography key={index} variant="body2" sx={{ color: 'text.secondary' }}>
                  • {feature}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Card>

      {/* Team Card */}
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <GroupsIcon sx={{ color: 'primary.main', mt: 0.5 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 1 }}>
              The Team
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
              Built by growers, for growers. Our team combines expertise in horticulture, sensor technology,
              and software engineering to create the ultimate grow management platform.
            </Typography>
          </Box>
        </Stack>
      </Card>

      {/* Version Info */}
      <Card variant="outlined" sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <UpdateIcon sx={{ color: 'primary.main', mt: 0.5 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 1 }}>
              Version Information
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Version: 2.0.0 Beta
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Last Updated: December 2024
              </Typography>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Have questions or need support? Visit our{' '}
              <Link href="#" underline="hover">documentation</Link> or{' '}
              <Link href="#" underline="hover">contact us</Link>.
            </Typography>
          </Box>
        </Stack>
      </Card>
    </Box>
  );
}

export default function AboutPage(props) {
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo');

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <DataModeProvider mode={isDemo ? DATA_MODES.DEMO : DATA_MODES.LIVE}>
          <GrowProvider>
            <SideMenu />
            <AppNavbar />
            <Box
              component="main"
              sx={(theme) => ({
                flexGrow: 1,
                backgroundColor: theme.vars
                  ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                  : alpha(theme.palette.background.default, 1),
                overflow: 'auto',
                minHeight: '100vh',
              })}
            >
              <Stack
                sx={{
                  pt: { xs: 10, md: 3 },
                  pb: 5,
                }}
              >
                <AboutContent />
              </Stack>
            </Box>
          </GrowProvider>
        </DataModeProvider>
      </Box>
    </AppTheme>
  );
}
